import { useId, useState } from 'react'
import { useSelector } from 'react-redux'

import styles from '@/pages/app/Settings/styles.module.scss'

import Button from '@/components/Button'
import TextArea from '@/components/TextArea'
import Tooltip from '@/components/Tooltip'
import { selectCurrentEnterprise, selectPlanType } from '@/store/features/user/userSlice'
import { EnterpriseSettings, PlanType, SettingType } from '@/types'

// Google Meet caps automated meeting-chat posts at 500 characters — the tightest ceiling of any
// platform we post into. 450 leaves headroom for clients that count emoji and other Unicode
// differently. Kept in lock-step with the backend's MAX_NOTETAKER_CONSENT_CHAT_MESSAGE_LENGTH.
//
// Enforced by `clampToCodePoints` on input, never by the textarea's own `maxLength`: HTML counts
// UTF-16 units, so that capped an emoji notice at half this and did it by silently refusing
// keystrokes — before the counter could redden or the limit be announced.
export const MAX_CONSENT_CHAT_MESSAGE_LENGTH = 450

// Mirrors desktop's `menus_notetaker_consent_notice_default_message`. Saving text equal to this
// clears the setting instead, so members keep getting the notice in their own language.
export const DEFAULT_CONSENT_CHAT_MESSAGE =
  "Hi everyone — quick note that I'm using Wispr Notetaker to transcribe this meeting so I can stay present. Let me know if you'd like me to turn it off."

const HELPER_TEXT = 'Posted to the meeting chat when the Notetaker starts. Users can’t edit this message.'

// The backend counts code points; JS `.length` counts UTF-16 units, so an emoji costs 2 there and
// 1 there. Counting differently is how a notice the portal accepts gets 400ed on save.
const codePointLength = (text: string) => Array.from(text).length
const clampToCodePoints = (text: string, max: number) => Array.from(text).slice(0, max).join('')

interface ConsentChatMessageSectionProps {
  message?: string | null
  isAdmin: boolean
  applySettingsPatch: (patch: Partial<EnterpriseSettings>) => Promise<void>
}

const ConsentChatMessageSection = ({ message, isAdmin, applySettingsPatch }: ConsentChatMessageSectionProps) => {
  const planType = useSelector(selectPlanType)
  const enterprise = useSelector(selectCurrentEnterprise)
  const saved = message ?? DEFAULT_CONSENT_CHAT_MESSAGE
  // Nullable, and never synced from `saved` by an effect. The effect this replaces fired on every
  // change to `message` — including the rollback of a FAILED save — and destroyed whatever the
  // admin had typed. Null also covers hydration: the section renders before settings load, so
  // `message` is undefined and `saved` is the English default; seeding state from that would let
  // Save write it as if the admin had chosen it.
  const [draft, setDraft] = useState<string | null>(null)
  // Local, not the page-wide save flag: Organization passes one `isSaveLoading` for every setting,
  // so an unrelated Notetaker toggle saving would disable (and blank) this field mid-edit.
  const [isSaving, setIsSaving] = useState(false)
  const counterId = useId()
  const helperId = useId()

  const value = draft ?? saved
  const isEditable = isAdmin && planType === PlanType.Enterprise
  const trimmed = value.trim()
  const isDirty = trimmed !== saved.trim()
  const isCustom = message !== null && message !== undefined
  const atLimit = codePointLength(value) >= MAX_CONSENT_CHAT_MESSAGE_LENGTH
  const tooltipText = !isAdmin
    ? 'Only admins can modify this'
    : planType !== PlanType.Enterprise
      ? 'Upgrade to Enterprise plan to modify this'
      : undefined

  // `null` is what makes each member receive the notice in their own language, so a byte-exact
  // match on the English default clears the setting rather than pinning the org to English.
  const patchValue = (text: string) => (text === DEFAULT_CONSENT_CHAT_MESSAGE.trim() ? null : text)

  const save = async (text: string | null) => {
    setIsSaving(true)
    try {
      await applySettingsPatch({ [SettingType.notetakerConsentChatMessage]: text })
    } finally {
      setIsSaving(false)
    }
  }

  // The draft is deliberately not cleared here. `applySettingsPatch` swallows its own error and
  // returns Promise<void>, so this cannot tell success from failure — but `isDirty` is derived, so
  // a successful save moves `saved` and the buttons disappear on their own, while a failed one
  // leaves both the draft and the buttons in place.
  const handleSave = () => save(patchValue(trimmed))

  const handleRestoreDefault = async () => {
    await save(null)
    setDraft(null)
  }

  return (
    <div className={styles.consentChatMessage} data-testid="notetaker-consent-chat-message">
      <Tooltip text={tooltipText} placement="top" hideArrow>
        <TextArea
          value={value}
          rows={3}
          isReadOnly={!isEditable}
          onInput={(text) => setDraft(clampToCodePoints(text, MAX_CONSENT_CHAT_MESSAGE_LENGTH))}
          aria-label={`Chat disclosure message for ${enterprise?.name || 'your organization'}`}
          aria-describedby={`${helperId} ${counterId}`}
        />
      </Tooltip>
      <div className={styles.consentChatMessageFooter}>
        <div className={styles.settingHelperText}>
          <span id={helperId}>
            {HELPER_TEXT}
            {/* Which state the org is actually in. Editing one character silently converts a
                multinational org to English-for-everyone, and without this the only way back was a
                byte-exact retype including the em dash and the curly apostrophe. */}
            <span className={styles.consentChatMessageState}>
              {isCustom
                ? ' Your organization uses a custom message, shown to every member in English.'
                : ' Your organization uses the default message, translated for each member.'}
            </span>
          </span>
          {isCustom && isEditable && !isDirty && (
            <Button title="Restore default" type="light" onClick={handleRestoreDefault} isDisabled={isSaving} />
          )}
        </div>
        <div className={styles.consentChatMessageActions}>
          <span
            id={counterId}
            className={`${styles.consentChatCounter} ${atLimit ? styles.consentChatCounterAtLimit : ''}`}
          >
            {codePointLength(value)} / {MAX_CONSENT_CHAT_MESSAGE_LENGTH}
          </span>
          {/* One-shot announcement at the limit. `aria-live` on the counter itself narrated every
              keystroke, which is what makes a live region unusable. */}
          {atLimit && (
            <span role="status" className={styles.visuallyHidden}>
              Character limit reached
            </span>
          )}
          {isDirty && (
            <>
              <Button title="Cancel" type="light" onClick={() => setDraft(null)} isDisabled={isSaving} />
              <Button
                title="Save"
                type="dark"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={!isEditable || trimmed.length === 0}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConsentChatMessageSection
